import { BaseTag, type TagSeverity } from '@/components/chariot-ui';
import {
  type RiskBucket,
  scoreToBucket,
  severityToBucket,
} from '@/components/RiskBadge.utils';
import { cn } from '@/utils/classname';

/**
 * All buckets use `text-white` UNIFORMLY. The medium bucket uses the dedicated
 * `chariot-status-medium-strong` token (`#7A6112` light / `#6F5C19` dark) so white
 * text clears AA (5.92:1 light, 6.52:1 dark). The shared `chariot-status-medium`
 * token was reverted to its canonical `#9A7B16` for the dark-text chips that
 * consume it (4.83:1 AA) — one token cannot serve both text colors, so white-text
 * pills use `-strong` while dark-text chips keep the reverted `medium`. Never use
 * the primary-text token here. (ENG-4341 Phase-7 reconciliation.)
 */
const BUCKET_CLASSES: Record<RiskBucket, string> = {
  critical:
    'bg-chariot-status-critical border-chariot-stroke-critical text-white',
  high: 'bg-chariot-status-high border-chariot-stroke-high text-white',
  medium:
    'bg-chariot-status-medium-strong border-chariot-stroke-medium text-white',
  low: 'bg-chariot-status-low border-chariot-stroke-low text-white',
  info: 'bg-chariot-status-info border-chariot-stroke-info text-white',
};

/** Neutral chip for a genuinely-absent score — distinct from a real tier-1 info badge. */
const UNKNOWN_CLASSES =
  'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-secondary';

export interface RiskBadgeProps {
  /** Numeric risk score. `undefined` renders the neutral unknown chip. */
  score?: number;
  /** Explicit severity; when provided it takes precedence over `score`. */
  severity?: TagSeverity;
  /** Pill size (maps to BaseTag). */
  size?: 'xs' | 'sm';
  /** Render the severity word visibly next to the score (always exposed to AT). */
  showLabel?: boolean;
  className?: string;
}

/**
 * Security risk score/severity rendered as a WCAG-AA pill. Wraps `BaseTag` for
 * chrome (never extends chariot-ui). Severity is always paired with a text
 * label/number — never encoded by colour alone.
 */
export function RiskBadge({
  score,
  severity,
  size = 'sm',
  showLabel = false,
  className,
}: RiskBadgeProps) {
  const hasScore = score !== undefined;
  const bucket: RiskBucket | null =
    severity !== undefined
      ? severityToBucket(severity)
      : hasScore
        ? scoreToBucket(score)
        : null;

  if (bucket === null) {
    return (
      <BaseTag size={size} className={cn(UNKNOWN_CLASSES, className)}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">Risk score unknown</span>
      </BaseTag>
    );
  }

  return (
    <BaseTag size={size} className={cn(BUCKET_CLASSES[bucket], className)}>
      {hasScore ? (
        <>
          <span>{score}</span>
          <span className={showLabel ? 'ml-1 uppercase' : 'sr-only'}>
            {showLabel ? bucket : `${bucket} risk`}
          </span>
        </>
      ) : (
        <span className="uppercase">{bucket}</span>
      )}
    </BaseTag>
  );
}
